import { useState, useMemo } from 'react';
import { Zap, X } from 'lucide-react';
import { palette } from '../../styles/tokens';
import type { ScheduleUser } from '../../components/schedule/scheduleHelpers';
import type { ScheduleEntry } from '../../types';
import type { BookingSource } from '../../data/enums';
import { entryStartMin, entryEndMin } from './appointmentUtils';

interface Service { id: string; name: string; duration_minutes: number; price: number; }

interface Props {
  isOpen: boolean;
  services: Service[];
  operators: ScheduleUser[];
  entries: ScheduleEntry[];
  onBook: (data: { clientName: string; serviceId: string; serviceName: string; operatorId: string; startTime: string; endTime: string; source: string }) => void;
  onClose: () => void;
}

function roundToQuarter(min: number): number {
  return Math.ceil(min / 15) * 15;
}

function minutesToTimeStr(min: number): string {
  return `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
}

function fmtTime12(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
}

export function WalkInModal({ isOpen, services, operators, entries, onBook, onClose }: Props) {
  const [clientName, setClientName] = useState('');
  const [serviceId, setServiceId] = useState('');
  const [operatorId, setOperatorId] = useState('');

  const now = new Date();
  const nowMin = roundToQuarter(now.getHours() * 60 + now.getMinutes());

  // Find first available operator (no booking covering NOW)
  const firstAvailable = useMemo(() => {
    for (const op of operators) {
      const busy = entries.some(e =>
        e.assigned_to?.includes(op.id) && e.status !== 'cancelled' &&
        entryStartMin(e) <= nowMin && entryEndMin(e) > nowMin,
      );
      if (!busy) return op.id;
    }
    return operators[0]?.id ?? '';
  }, [operators, entries, nowMin]);

  // Set defaults
  useState(() => { setOperatorId(firstAvailable); });

  const selectedService = services.find(s => s.id === serviceId);
  const endMin = selectedService ? nowMin + selectedService.duration_minutes : nowMin + 30;

  const canBook = serviceId && (operatorId || operators.length === 0);

  const handleBook = () => {
    if (!canBook || !selectedService) return;
    onBook({
      clientName: clientName.trim() || 'Walk-in',
      serviceId: selectedService.id,
      serviceName: selectedService.name,
      operatorId: operatorId || operators[0]?.id || '',
      startTime: minutesToTimeStr(nowMin),
      endTime: minutesToTimeStr(endMin),
      source: 'walk_in' satisfies BookingSource,
    });
    setClientName(''); setServiceId(''); setOperatorId(firstAvailable);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 100 }} />

      {/* Modal */}
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: 360, background: palette.white, borderRadius: 16,
        boxShadow: '0 8px 32px rgba(0,0,0,0.15)', zIndex: 101,
        padding: 24,
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Zap size={18} color="#D46A0E" />
            <span style={{ fontSize: 16, fontWeight: 600, color: palette.g800 }}>Quick Walk-In</span>
          </div>
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 14, border: 'none', background: palette.g100, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={14} color={palette.g500} />
          </button>
        </div>

        {/* Client (optional) */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 500, color: palette.g500, marginBottom: 4, display: 'block' }}>Client name (optional)</label>
          <input value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Walk-in client"
            style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${palette.g200}`, fontSize: 13.5, fontFamily: 'inherit', outline: 'none', color: palette.g800, boxSizing: 'border-box' as const }} />
        </div>

        {/* Service (required) */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 500, color: palette.g500, marginBottom: 4, display: 'block' }}>Service *</label>
          <select value={serviceId} onChange={e => setServiceId(e.target.value)}
            style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${palette.g200}`, fontSize: 13.5, fontFamily: 'inherit', color: palette.g800, background: palette.white }}>
            <option value="">Select service...</option>
            {services.map(s => <option key={s.id} value={s.id}>{s.name} ({s.duration_minutes}min · ${s.price})</option>)}
          </select>
        </div>

        {/* Operator (required) */}
        {operators.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, fontWeight: 500, color: palette.g500, marginBottom: 4, display: 'block' }}>Operator *</label>
            <select value={operatorId || firstAvailable} onChange={e => setOperatorId(e.target.value)}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${palette.g200}`, fontSize: 13.5, fontFamily: 'inherit', color: palette.g800, background: palette.white }}>
              {operators.map(op => <option key={op.id} value={op.id}>{op.first_name} {op.last_name}</option>)}
            </select>
          </div>
        )}

        {/* Time */}
        <div style={{ fontSize: 13, color: palette.g500, marginBottom: 20 }}>
          Start time: <strong style={{ color: palette.g800 }}>{fmtTime12(nowMin)}</strong>
        </div>

        {/* Book button */}
        <button onClick={handleBook} disabled={!canBook}
          style={{
            width: '100%', padding: '12px 0', borderRadius: 10, border: 'none',
            background: canBook ? palette.accent : palette.g200,
            color: canBook ? palette.white : palette.g400,
            fontSize: 14, fontWeight: 600, cursor: canBook ? 'pointer' : 'default',
            fontFamily: 'inherit',
          }}>
          Add Walk-In
        </button>
      </div>
    </>
  );
}
